/** @jsxImportSource preact */
import type { ProductCardData } from '../types';
import { sanitizeUrl } from '../services/sanitize';

interface ProductCardProps {
  product: ProductCardData;
  /**
   * Tapping the card opens the product detail view. The editorial
   * carousel (W-1 north-star) has NO inline add button — the whole card
   * is the affordance and the add-to-cart CTA lives in the detail view.
   */
  onView: (product: ProductCardData) => void;
}

function formatPrice(product: ProductCardData): string {
  if (typeof product.price !== 'number' || !Number.isFinite(product.price)) {
    return '';
  }
  const amount = product.price.toLocaleString('es-AR');
  return product.currency ? `${product.currency} ${amount}` : `$${amount}`;
}

/**
 * Editorial product card: a 3:4 image over a neutral field, the name in
 * uppercase with tracking, and the price. No button — tapping the card
 * emits a `view-product` intent (handled by the widget host, which opens
 * the product detail view).
 *
 * Image URLs pass through `sanitizeUrl`; anything that is not an absolute
 * http(s) URL renders the placeholder field instead. Never uses
 * dangerouslySetInnerHTML — Preact escapes the name by default.
 */
export function ProductCard({ product, onView }: ProductCardProps) {
  const imageUrl = sanitizeUrl(product.image_url || '');
  const priceStr = formatPrice(product);

  return (
    <button
      type="button"
      class="lc-product-card"
      data-product-id={product.product_id}
      aria-label={product.name || 'Ver producto'}
      onClick={(e) => {
        e.preventDefault();
        onView(product);
      }}
    >
      <div class="lc-product-image">
        {imageUrl ? (
          <img src={imageUrl} alt={product.name || ''} loading="lazy" />
        ) : (
          <div class="lc-product-no-image" data-empty="true" aria-hidden="true" />
        )}
      </div>
      <div class="lc-product-info">
        <span class="lc-product-name">{product.name}</span>
        {priceStr ? <span class="lc-product-price">{priceStr}</span> : null}
      </div>
    </button>
  );
}
